import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import MaxWidth from '@/components/common/layout/MaxWidth';
import Description from '@/components/localities/Description';
import Hero from '@/components/localities/Hero';
import SectionTitle from '@/components/localities/SectionTitle';
import { TOP_MENU_RVS } from '@/components/ui/SearchHeader/SearchHeader';
import { setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import { trackEvent } from '@/services/track-event';

import css from './LocalityDirectoryByTypeTemplate.module.css';

const LocalityDirectoryByTypeTemplate = props => {
  const {
    title,
    breadcrumbs,
    content,
    locality,
    top_localities,
    top_localities_type,
    other_directories,
    url_path,
  } = props.metadata;

  const { region_code } = locality;

  const topParks = (top_localities || []).filter(Boolean);
  const crossLinks = (other_directories || []).filter(Boolean);

  const topParkType = top_localities_type || 'campground';
  const parksWithDescriptions = topParksWithDescriptions(topParks);
  const parksWithLinks = topParksWithLinks(topParks);

  let pageTitle = title;
  let pageDescription = `Find the perfect RV rental in ${locality.title}. Simple, easy and fully insured.`;
  const sectionTitle = sectionTitleByParkType(topParkType);

  const dispatch = useDispatch();

  useEffect(() => {
    if (locality?.title) {
      dispatch(
        setHeaderSearchFilter({
          address: locality?.title,
          skip_defaults: true,
        }),
      );
    }
  }, [dispatch, locality?.title]);

  useEffect(() => {
    trackEvent({
      event: 'Locality : Viewed page',
      pageType: 'Locality directory by type SRP',
    });
  }, []);

  return (
    <div className={css.localityDirectory}>
      <Hero
        title={pageTitle}
        description={pageDescription}
        regionCode={region_code}
        urlPath={url_path}
        breadcrumbs={breadcrumbs}
        lockedSearchCategory={TOP_MENU_RVS}
        localitySearchFilters={{ skip_defaults: 'true' }}
      />

      {content && content !== '' && (
        <MaxWidth>
          <SectionTitle>Discover {title}</SectionTitle>
          <Description description={content} />
        </MaxWidth>
      )}

      <MaxWidth className="mb-8">
        <SectionTitle>{sectionTitle}</SectionTitle>

        {parksWithDescriptions.length > 0 && (
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {parksWithDescriptions.map(park => {
              let description = park.text;
              return (
                <div
                  className="p-6 text-gray-900 border border-gray-200 rounded shadow hover:shadow-md"
                  key={park.url}>
                  <a href={park.url}>
                    <h3 className="text-xl font-medium text-gray-900">{park.title}</h3>
                  </a>
                  <p className="mb-2 text-gray-500">{park.listing_count} RV rentals</p>
                  <div className={css.truncate} dangerouslySetInnerHTML={{ __html: description }} />
                </div>
              );
            })}
          </div>
        )}

        {parksWithLinks.length > 0 && (
          <div className="mt-8 grid grid-cols-2 gap-8 lg:grid-cols-3">
            {parksWithLinks.map(park => {
              return (
                <a
                  href={park.url}
                  key={park.title}
                  className="p-6 text-gray-900 border border-gray-200 rounded shadow hover:shadow-md">
                  <h3 className="text-xl font-medium">{park.title}</h3>
                  <p className="mb-2 text-gray-500">{park.listing_count} RV rentals</p>
                </a>
              );
            })}
          </div>
        )}
      </MaxWidth>

      {crossLinks && (
        <MaxWidth className="mt-12 mb-6">
          <SectionTitle>Discover {title}</SectionTitle>

          <div className="grid grid-cols-1 gap-2 sm:gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-4">
            {crossLinks.map(city => {
              return (
                <div className="md:pr-4" key={city.url}>
                  <a href={city.url} className="text-gray-900">
                    <h3 className="font-medium font-lg font">{city.title}</h3>
                  </a>
                </div>
              );
            })}
          </div>
        </MaxWidth>
      )}
    </div>
  );
};

export default LocalityDirectoryByTypeTemplate;

const topParksWithDescriptions = parks => parks.filter(park => (park.text ? park : null));

const topParksWithLinks = parks => parks.filter(park => (!park.text ? park : null));

const sectionTitleByParkType = type => {
  let title = 'Parks and Campgrounds';
  switch (type) {
    case 'campground':
      title = 'RV parks and campgrounds';
      break;

    case 'park':
      title = 'State Parks';
      break;
  }

  return title;
};
